import axiosNest from '../api/axiosNest';
import type { WriteDetails } from '../order/viaWriteDetailsApi';

/**
 * Prezlecenia z maili i dokumentów (03.11.2026, przełącznik
 * `preOrderDocuments`, S-03.11-01, paczka CS i Administracja). Odpowiada
 * `pre-order/email-links.ts`, `send-readiness.ts`, `document-formats.ts`
 * i `spreadsheet-preview.ts` w API.
 */
export const FEATURE_PRE_ORDER_DOCUMENTS = 'preOrderDocuments';

// ── stan „gotowe do wysłania” ──────────────────────────────────────────────

export interface ReadinessItem {
  /** Klucz pola jak w API (`station.1.zip`, `orderingParty.adres`, `price`…). */
  key: string;
  group: 'acceptance' | 'preOrder' | 'orderingParty' | 'shipment' | 'station' | 'forklift' | 'file';
  label: string;
}

export interface SendReadiness {
  ready: boolean;
  acceptanceReady: boolean;
  missing: ReadinessItem[];
  fileErrors: string[];
  stationCount: number;
}

/** Stan z odpowiedzi API; inny kształt = brak (bez wywrotki). */
export function readSendReadiness(raw: unknown): SendReadiness | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<SendReadiness>;
  if (typeof r.ready !== 'boolean') return null;
  return {
    ready: r.ready,
    acceptanceReady: r.acceptanceReady === true,
    missing: Array.isArray(r.missing) ? r.missing : [],
    fileErrors: Array.isArray(r.fileErrors) ? r.fileErrors : [],
    stationCount: typeof r.stationCount === 'number' ? r.stationCount : 0,
  };
}

/** Czy klucz pola jest na liście braków (pole punktu — z numerem punktu). */
export function isMissing(readiness: SendReadiness | null, key: string): boolean {
  return !!readiness && readiness.missing.some((m) => m.key === key);
}

/** Wiersz listy: komplet, braki i liczniki (`documents` z `GET /pre-orders`). */
export interface ListDocuments {
  ready: boolean;
  missingCount: number;
  acceptanceReady: boolean;
  emailCount: number;
  fileCount: number;
  origin: 'email' | 'copy' | 'manual';
  orderNumber: string | null;
}

export function readListDocuments(raw: unknown): ListDocuments | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Partial<ListDocuments>;
  if (typeof d.ready !== 'boolean') return null;
  return {
    ready: d.ready,
    missingCount: Number(d.missingCount ?? 0),
    acceptanceReady: d.acceptanceReady === true,
    emailCount: Number(d.emailCount ?? 0),
    fileCount: Number(d.fileCount ?? 0),
    origin: d.origin === 'email' || d.origin === 'copy' ? d.origin : 'manual',
    orderNumber: typeof d.orderNumber === 'string' ? d.orderNumber : null,
  };
}

export const ORIGIN_LABELS: Record<ListDocuments['origin'], string> = {
  email: 'z maila',
  copy: 'kopia',
  manual: 'wpisane ręcznie',
};

// ── maile ↔ prezlecenia ────────────────────────────────────────────────────

export type LinkRole = 'SOURCE' | 'SUPPLEMENT' | 'CORRESPONDENCE' | 'CHANGE' | 'DUPLICATE_OF';

/** Role, które wybiera człowiek (API przyjmuje tylko te trzy). */
export const MANUAL_LINK_ROLES: Array<{ id: LinkRole; name: string }> = [
  { id: 'SOURCE', name: 'źródło' },
  { id: 'SUPPLEMENT', name: 'uzupełnienie' },
  { id: 'CORRESPONDENCE', name: 'korespondencja' },
];

export const MIN_UNLINK_REASON = 10;

export interface PreOrderEmail {
  emailId: number;
  role: string;
  roleLabel: string;
  origin: 'manual' | 'extraction' | 'legacy';
  originLabel: string;
  confidence: number | null;
  linkedBy: string | null;
  linkedAt: string;
  note: string | null;
  subject: string | null;
  fromName: string | null;
  fromAddress: string | null;
  receivedAt: string | null;
  sentAt: string | null;
  attachmentCount: number;
}

export interface EmailPreOrder {
  preOrderId: number;
  role: string;
  roleLabel: string;
  origin: 'manual' | 'extraction' | 'legacy';
  originLabel: string;
  confidence: number | null;
  linkedBy: string | null;
  linkedAt: string;
  clientName: string | null;
  customerOrderNumber: string | null;
  loadingCity: string | null;
  unloadingCity: string | null;
  loadingDate: string | null;
  status: string;
  orderNumber: string | null;
  archived: boolean;
}

const asArray = <T,>(data: unknown): T[] => (Array.isArray(data) ? (data as T[]) : []);

export async function fetchPreOrderEmails(preOrderId: number | string): Promise<PreOrderEmail[]> {
  const { data } = await axiosNest.get(`/pre-orders/${preOrderId}/emails`);
  return asArray<PreOrderEmail>(data);
}

export async function linkEmail(
  preOrderId: number | string,
  body: { emailId: number; role: LinkRole; note?: string },
): Promise<PreOrderEmail[]> {
  const { data } = await axiosNest.post(`/pre-orders/${preOrderId}/emails`, body);
  return asArray<PreOrderEmail>(data);
}

export async function unlinkEmail(preOrderId: number | string, emailId: number, reason: string): Promise<PreOrderEmail[]> {
  const { data } = await axiosNest.delete(`/pre-orders/${preOrderId}/emails/${emailId}`, { data: { reason } });
  return asArray<PreOrderEmail>(data);
}

export async function fetchEmailPreOrders(emailId: number | string): Promise<EmailPreOrder[]> {
  const { data } = await axiosNest.get(`/emails/${emailId}/pre-orders`);
  return asArray<EmailPreOrder>(data);
}

export async function linkPreOrderToEmail(
  emailId: number | string,
  body: { preOrderId: number; role: LinkRole; note?: string },
): Promise<EmailPreOrder[]> {
  const { data } = await axiosNest.post(`/emails/${emailId}/pre-orders`, body);
  return asArray<EmailPreOrder>(data);
}

export interface EmailOption {
  id: number;
  subject: string | null;
  fromName: string | null;
  receivedAt: string | null;
}

/** Wyszukiwarka maili do powiązania — skrzynka (`GET /emails`, filtr `q`). */
export async function searchEmails(q: string): Promise<EmailOption[]> {
  const { data } = await axiosNest.get('/emails', {
    params: { filter: JSON.stringify(q.trim() ? { q: q.trim() } : {}), range: JSON.stringify([0, 9]), sort: JSON.stringify(['receivedAt', 'DESC']) },
  });
  return asArray<EmailOption>(data).map((e) => ({ id: e.id, subject: e.subject ?? null, fromName: e.fromName ?? null, receivedAt: e.receivedAt ?? null }));
}

export interface PreOrderOption {
  id: number;
  clientName: string | null;
  relation: string | null;
  customerOrderNumber: string | null;
}

/** Wyszukiwarka prezleceń do powiązania z mailem (`GET /pre-orders`, filtr `q`). */
export async function searchPreOrders(q: string): Promise<PreOrderOption[]> {
  const { data } = await axiosNest.get('/pre-orders', {
    params: { filter: JSON.stringify(q.trim() ? { q: q.trim() } : {}), range: JSON.stringify([0, 9]), sort: JSON.stringify(['createdAt', 'DESC']) },
  });
  return asArray<PreOrderOption>(data).map((p) => ({
    id: p.id,
    clientName: p.clientName ?? null,
    relation: p.relation ?? null,
    customerOrderNumber: p.customerOrderNumber ?? null,
  }));
}

// ── pola pliku dla importera na prezleceniu ────────────────────────────────

export interface PreOrderWriteDetails {
  preOrderId: number;
  details: WriteDetails;
  readiness: SendReadiness | null;
  refusal: string | null;
}

function readWriteDetailsView(data: unknown, preOrderId: number | string): PreOrderWriteDetails {
  const d = (data ?? {}) as Partial<PreOrderWriteDetails>;
  return {
    preOrderId: Number(preOrderId),
    details: (d.details as WriteDetails) ?? {},
    readiness: readSendReadiness(d.readiness),
    refusal: typeof d.refusal === 'string' ? d.refusal : null,
  };
}

export async function fetchPreOrderWriteDetails(preOrderId: number | string): Promise<PreOrderWriteDetails> {
  const { data } = await axiosNest.get(`/pre-orders/${preOrderId}/write-details`);
  return readWriteDetailsView(data, preOrderId);
}

export async function savePreOrderWriteDetails(preOrderId: number | string, details: WriteDetails): Promise<PreOrderWriteDetails> {
  const { data } = await axiosNest.put(`/pre-orders/${preOrderId}/write-details`, { details });
  return readWriteDetailsView(data, preOrderId);
}

// ── podgląd arkusza ────────────────────────────────────────────────────────

export interface TableSheet {
  name: string;
  rows: Array<Array<string | null>>;
  truncated: boolean;
}

export interface TablePreview {
  sheets: TableSheet[];
  moreSheets: boolean;
  note: string;
}

/**
 * Zdjęcie HEIC przeliczone na serwerze do JPEG/PNG (03.11.2026) — pierwszy
 * obraz pliku i liczba obrazów (`X-Preview-Images`).
 */
export async function fetchHeicPreview(
  preOrderId: number | string,
  origin: { emailId: number } | 'client',
  fileId: number,
): Promise<{ blob: Blob; imageCount: number }> {
  const url =
    origin === 'client'
      ? `/pre-orders/${preOrderId}/source/files/${fileId}/preview`
      : `/pre-orders/${preOrderId}/source/emails/${origin.emailId}/attachments/${fileId}/preview`;
  const res = await axiosNest.get<Blob>(url, { responseType: 'blob' });
  const count = Number((res.headers as Record<string, unknown> | undefined)?.['x-preview-images'] ?? 1);
  return { blob: res.data, imageCount: Number.isFinite(count) && count > 0 ? count : 1 };
}

/** Komunikat błędu z odpowiedzi w postaci bloba (trasa podglądu oddaje obraz albo JSON z błędem). */
export async function blobErrorMessage(e: unknown): Promise<string | null> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof (data as Blob).text === 'function') {
    try {
      const body = JSON.parse(await (data as Blob).text()) as { message?: unknown };
      return typeof body.message === 'string' ? body.message : null;
    } catch {
      return null;
    }
  }
  return null;
}

export async function fetchSourceTable(
  preOrderId: number | string,
  origin: { emailId: number } | 'client',
  fileId: number,
): Promise<TablePreview> {
  const url =
    origin === 'client'
      ? `/pre-orders/${preOrderId}/source/files/${fileId}/table`
      : `/pre-orders/${preOrderId}/source/emails/${origin.emailId}/attachments/${fileId}/table`;
  const { data } = await axiosNest.get<Partial<TablePreview>>(url);
  return {
    sheets: Array.isArray(data?.sheets) ? (data!.sheets as TableSheet[]) : [],
    moreSheets: data?.moreSheets === true,
    note: typeof data?.note === 'string' ? data.note : '',
  };
}
